
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions } from 'vuex';
    import _ from 'lodash';
    import api from '@/services/Api';
    import axios, { AxiosResponse, CancelTokenSource } from 'axios';
    import { DataTableHeaders, GridConfig, GridDataOptions, GridFilters, GridParams, GridSorters } from '@/types';
    import { DataPagination } from 'vuetify';
    import { VGrid } from '@/types/Components';

    @Component({
        methods: {
            ...mapActions('grids', ['loadGrid', 'saveGrid']),
        },
    })
    export default class Grid<
        T,
        R extends { body: { data: Partial<T>[]; total: number } }
    > extends Vue implements VGrid<T> {
        @Prop({ required: true }) public config!: GridConfig<T>;
        @Prop({ default: () => [] }) public filters!: GridFilters<T>;
        @Prop({ default: () => [10, 25, 50] }) public itemsPerPageOptions!: number[];
        @Prop({ default: () => true }) public showFirstLastPage!: boolean;
        @Prop({ default: () => 500 }) public loadDebounceDelay!: number;

        public $refs!: {
            table: Vue;
        };

        public saveGrid!: (payload: { key: string, data: GridParams<T> }) => void;
        public loadGrid!: (payload: { key: string }) => Promise<GridParams<T>>;

        public options: GridDataOptions<T> = {
            groupBy: [],
            groupDesc: [],
            itemsPerPage: 25,
            multiSort: true,
            mustSort: false,
            page: 1,
            sortBy: [],
            sortDesc: [],
        };

        public pagination: DataPagination = {
            page: 1,
            itemsPerPage: 25,
            pageStart: 1,
            pageStop: 10,
            pageCount: 10,
            itemsLength: 25,
        };

        public items: (T | Partial<T>)[] = [];
        public loading = false;
        public loadingManualRefresh = false;
        public totalCount = 0;
        public paginationInfo = '-';

        protected source: CancelTokenSource | null = null;
        protected apiCall: _.DebouncedFunc<() => void> | null = null;

        public get headers(): DataTableHeaders<T> {
            return this.config.headers;
        }

        public get sorters(): GridSorters<T> {
            return this.options.sortBy.map((by, index) => {
                return {
                    property: by,
                    direction: this.options.sortDesc[index] ? 'DESC' : 'ASC',
                };
            });
        }

        @Watch('loading')
        protected onChangeLoading(loading: boolean): void {
            if (loading) {
                this.$nextTick(() => {
                    const [thead, theadLoader] = this.$refs.table.$el.querySelectorAll('thead');
                    if (thead && theadLoader) {
                        thead.append(...theadLoader.children);
                    }
                });
            } else {
                this.$nextTick(() => {
                    const thead = this.$refs.table.$el.querySelector('thead');
                    const tr = thead?.querySelector('.v-data-table__progress');
                    tr?.remove();
                });
            }
        }

        public createParams(): GridParams<T> {
            const params: GridParams<T> = {
                page: this.pagination.page ?? 1,
                start: this.pagination.pageStart ?? 1,
                limit: this.pagination.itemsPerPage ?? 25,
            };

            const sorters = this.sorters;
            const filters = this.filters;

            if (sorters.length) params.sort = sorters;
            if (filters.length) params.filter = filters;

            return params;
        }

        public beforeMount(): void {
            this.loadGrid({ key: this.config.id })
                .then((storage) => {
                    if (storage) {
                        this.pagination.page = storage.page ?? 1;
                        this.pagination.pageStart = storage.start ?? 1;
                        this.pagination.itemsPerPage = storage.limit ?? 25;

                        this.options.page = storage.page ?? 1;
                        this.options.itemsPerPage = storage.limit ?? 25;

                        this.options.sortBy = [];
                        this.options.sortDesc = [];

                        ((storage.sort ?? []) as GridSorters<T>).forEach((sort) => {
                            this.options.sortBy.push(sort.property as (keyof T));
                            this.options.sortDesc.push(sort.direction === 'DESC');
                        });

                        this.$emit('load:storage-filters', storage.filter);
                    }
                });
        }

        public load(filterReset?: boolean): void {
            if (filterReset) {
                this.pagination.page = 1;
            }

            this.loading = true;

            if (!this.apiCall) {
                this.apiCall = _.debounce(() => {
                    if (this.source) {
                        this.source.cancel('cancel(v-grid) ' + this.config.remote);
                    }

                    this.source = axios.CancelToken.source();

                    const params = this.createParams();
                    this.saveGrid({ key: this.config.id, data: params });

                    api.post(this.config.remote, params, { cancelToken: this.source.token })
                        .then(({ data: { body: { data, total} } }: AxiosResponse<R>) => {
                            const ItemClass = this.config.class;

                            this.items = ItemClass
                                ? data.map((item) => new ItemClass(item))
                                : data;
                            this.totalCount = total;

                            this.$emit('load', {
                                items: this.items,
                                totalCount: this.totalCount
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            this.$emit('load:error', error);
                        })
                        .finally(() => {
                            this.source = null;
                            this.loading = false;
                            this.loadingManualRefresh = false;
                            this.updatePaginationInfo();
                        });
                }, this.loadDebounceDelay);
            }

            this.apiCall();
        }

        protected onClickRefresh(): void {
            this.loadingManualRefresh = true;
            this.load();
        }

        protected onPagination(pagination: DataPagination): void {
            this.pagination = pagination;

            if (!this.loading) {
                this.updatePaginationInfo();
            }
        }

        protected updatePaginationInfo(): void {
            const itemsLength = this.pagination?.itemsLength;

            if (itemsLength && this.pagination?.itemsPerPage) {
                const pageStart = (this.pagination.pageStart ?? 1) + 1;
                const pageStop = itemsLength < this.pagination.pageStop || this.pagination.pageStop < 0
                    ? itemsLength
                    : this.pagination.pageStop;

                this.paginationInfo = `${pageStart}-${pageStop} von ${itemsLength}`;
            } else {
                this.paginationInfo = '–';
            }
        }
    }
